import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, MapPin, BookOpen, DollarSign, SlidersHorizontal, GraduationCap, AlertCircle } from 'lucide-react';
import { buildPageSEO, breadcrumbSchema } from '@/utils/seo';
import TutorCard from '../components/common/TutorCard';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Modal from '../components/Modal/Modal';
import WishlistCaseSelectionModal from '../components/Modal/WishlistCaseSelectionModal';
import SearchableDropdown from '../components/common/SearchableDropdown';
import SearchableInput from '../components/common/SearchableInput';
import DualRangeSlider from '../components/common/DualRangeSlider';
import { Pagination } from '@tutor/ui';
import { CaseCardSkeletonGrid } from '../components/skeletons';
import { MALAYSIAN_STATES, EDUCATION_LEVELS, TUITION_EXPERIENCE_YEARS } from '../constants/dropdownOptions';
import { getSubjectsForLevel } from '@tutor/types';
import { useTutors } from '../hooks/api/useTutors';
import {
  getOptimisticWishlistState,
  useTutorWishlistOverlayMap,
} from '../hooks/api/useWishlistTutors';
import { useAuthContext, useModal } from '@/contexts';
import type { GetTutorsParams } from '../types/api';
import { useI18n } from '@/i18n';
import { localizeStringList, localizeText } from '@/i18n/options';

const getFiltersFromSearchParams = (searchParams: URLSearchParams) => {
  const location = searchParams.get('location') || '';
  const level = searchParams.get('level') || '';
  const subject = searchParams.get('subject') || '';
  const priceMin = searchParams.get('priceMin') || '';
  const priceMax = searchParams.get('priceMax') || '';
  const yearsOfExperience = searchParams.get('yearsOfExperience') || '';
  const gender = searchParams.get('gender') || '';
  const tuitionType = searchParams.get('tuitionType') || '';
  const page = Math.max(parseInt(searchParams.get('page') || '1', 10) || 1, 1);

  return {
    filters: {
      location,
      level,
      subject,
    },
    advancedFilters: {
      yearsOfExperience,
      priceMin,
      priceMax,
      gender,
      tuitionType,
    },
    page,
  };
};

const AllTutorsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { locale } = useI18n();
  const initialSearchState = useMemo(() => getFiltersFromSearchParams(searchParams), [searchParams]);
  const [currentPage, setCurrentPage] = useState(initialSearchState.page);
  const isSyncingFromUrlRef = useRef(false);
  const { profile, firebaseUser } = useAuthContext();
  const { openLoginModal } = useModal();
  const isZh = locale === 'zh-CN';
  
  const [filters, setFilters] = useState(initialSearchState.filters);

  const [advancedFilters, setAdvancedFilters] = useState(initialSearchState.advancedFilters);

  const [tempAdvancedFilters, setTempAdvancedFilters] = useState(initialSearchState.advancedFilters);

  const updateFilters = (nextFilters: typeof filters) => {
    setFilters(nextFilters);
    setCurrentPage(1);
  };

  // Initialize filters from URL query parameters
  useEffect(() => {
    const nextState = getFiltersFromSearchParams(searchParams);
    isSyncingFromUrlRef.current = true;

    setFilters((prev) => (
      prev.location === nextState.filters.location &&
      prev.level === nextState.filters.level &&
      prev.subject === nextState.filters.subject
        ? prev
        : nextState.filters
    ));
    setAdvancedFilters((prev) => (
      prev.yearsOfExperience === nextState.advancedFilters.yearsOfExperience &&
      prev.priceMin === nextState.advancedFilters.priceMin &&
      prev.priceMax === nextState.advancedFilters.priceMax &&
      prev.gender === nextState.advancedFilters.gender &&
      prev.tuitionType === nextState.advancedFilters.tuitionType
        ? prev
        : nextState.advancedFilters
    ));
    setTempAdvancedFilters((prev) => (
      prev.yearsOfExperience === nextState.advancedFilters.yearsOfExperience &&
      prev.priceMin === nextState.advancedFilters.priceMin &&
      prev.priceMax === nextState.advancedFilters.priceMax &&
      prev.gender === nextState.advancedFilters.gender &&
      prev.tuitionType === nextState.advancedFilters.tuitionType
        ? prev
        : nextState.advancedFilters
    ));
    setCurrentPage((prev) => (prev === nextState.page ? prev : nextState.page));
  }, [searchParams]);

  const [showAdvanced, setShowAdvanced] = useState(false);

  const persistedSearchParams = useMemo(() => {
    const params = new URLSearchParams();

    if (filters.location) params.set('location', filters.location);
    if (filters.level) params.set('level', filters.level);
    if (filters.subject) params.set('subject', filters.subject);
    if (advancedFilters.priceMin) params.set('priceMin', advancedFilters.priceMin);
    if (advancedFilters.priceMax) params.set('priceMax', advancedFilters.priceMax);
    if (advancedFilters.yearsOfExperience) params.set('yearsOfExperience', advancedFilters.yearsOfExperience);
    if (advancedFilters.gender) params.set('gender', advancedFilters.gender);
    if (advancedFilters.tuitionType) params.set('tuitionType', advancedFilters.tuitionType);
    if (currentPage > 1) params.set('page', String(currentPage));

    return params;
  }, [advancedFilters, currentPage, filters]);

  useEffect(() => {
    if (isSyncingFromUrlRef.current) {
      isSyncingFromUrlRef.current = false;
      return;
    }

    const next = persistedSearchParams.toString();
    const current = searchParams.toString();

    if (next !== current) {
      setSearchParams(persistedSearchParams, { replace: true });
    }
  }, [persistedSearchParams, searchParams, setSearchParams]);

  // Get the filtered subjects based on the selected level
  const filteredSubjects = useMemo(() => {
    if (!filters.level) return [];
    return localizeStringList(locale, getSubjectsForLevel(filters.level));
  }, [filters.level, locale]);

  // Build query parameters
  const queryParams: GetTutorsParams = useMemo(
    () => ({
      page: currentPage,
      limit: 12,
      ...(filters.location && { location: filters.location.toLowerCase().replace(/\s+/g, '_') }),
      ...(filters.level && { level: filters.level as GetTutorsParams['level'] }),
      ...(filters.subject && { subject: filters.subject }),
      ...(advancedFilters.yearsOfExperience !== '' && {
        minExperience: parseInt(advancedFilters.yearsOfExperience),
        maxExperience: parseInt(advancedFilters.yearsOfExperience),
      }),
      ...(advancedFilters.priceMin && { minPrice: parseInt(advancedFilters.priceMin) }),
      ...(advancedFilters.priceMax && { maxPrice: parseInt(advancedFilters.priceMax) }),
      ...(advancedFilters.gender && { gender: advancedFilters.gender as GetTutorsParams['gender'] }),
      ...(advancedFilters.tuitionType && { tuitionType: advancedFilters.tuitionType as GetTutorsParams['tuitionType'] }),
    }),
    [currentPage, filters, advancedFilters]
  );

  // Use React Query Hook to fetch tutors
  const { data, isLoading, error } = useTutors(queryParams);

  const wishlistOverlayMap = useTutorWishlistOverlayMap();
  const [wishlistTutor, setWishlistTutor] = useState<{ id: string; name: string } | null>(null);

  const handleToggleWishlist = (tutorId: string, tutorName: string) => {
    if (!firebaseUser) {
      openLoginModal();
      return;
    }

    if (!profile || profile.role !== 'parent') {
      toast.error(isZh ? '只有家长可以收藏导师' : 'Only parents can like tutors');
      return;
    }

    if (wishlistOverlayMap.has(tutorId)) return;
    setWishlistTutor({ id: tutorId, name: tutorName });
  };

  const tutors = data?.tutors ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  // Handle search/filter button click
  const handleSearch = () => {
    setCurrentPage(1);
  };

  const seo = buildPageSEO(
    isZh ? '浏览马来西亚优质导师' : 'Browse Qualified Tutors in Malaysia',
    isZh
      ? '浏览马来西亚各地已审核导师，按科目、地点、程度和价格筛选合适导师。'
      : 'Find verified tutors for Primary, Secondary, Form 6, and International school subjects across Malaysia. Filter by subject, location, and price.',
    '/tutors',
  );

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.canonical} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.canonical} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Tutors', path: '/tutors' },
          ]))}
        </script>
      </Helmet>
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white py-20 flex items-center justify-center min-h-[26vh]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{isZh ? '寻找最适合你的导师' : 'Find Your Perfect Tutor'}</h1>
        </div>
      </section>

      {/* Search Section - Overlapping */}
      <section className="relative -mt-10 mb-20 md:mb-24 z-sticky">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Search Form Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-visible">

              {/* Search Form */}
              <div className="p-4 md:p-6 lg:p-8">
                <div className="space-y-3 md:space-y-0 md:flex md:flex-wrap md:gap-3 lg:flex lg:flex-nowrap lg:gap-4 lg:items-end">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      {isZh ? '地点' : 'Location'}
                    </label>
                    <SearchableInput
                      options={MALAYSIAN_STATES}
                      value={filters.location}
                      onChange={(value) => updateFilters({ ...filters, location: String(value) })}
                      placeholder={isZh ? '选择地点' : 'Select location'}
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <GraduationCap className="inline h-4 w-4 mr-1" />
                      {isZh ? '程度' : 'Level'}
                    </label>
                    <SearchableDropdown
                      options={EDUCATION_LEVELS}
                      value={filters.level}
                      onChange={(value) => updateFilters({ ...filters, level: String(value), subject: '' })}
                      placeholder={isZh ? '选择程度' : 'Select level'}
                      searchable={true}
                      truncate={true}
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <BookOpen className="inline h-4 w-4 mr-1" />
                      {isZh ? '科目' : 'Subject'}
                    </label>
                    <SearchableDropdown
                      options={filteredSubjects}
                      value={filters.subject}
                      onChange={(value) => updateFilters({ ...filters, subject: String(value) })}
                      placeholder={filters.level ? (isZh ? '选择科目' : 'Select subject') : (isZh ? '请先选择程度' : 'Select a level first')}
                      searchable={true}
                      disabled={!filters.level}
                    />
                  </div>

                  {/* Buttons Container */}
                  <div className="flex gap-2 md:mt-6 lg:mt-0 lg:shrink-0">
                    <button onClick={() => {
                      setTempAdvancedFilters(advancedFilters);
                      setShowAdvanced(true);
                    }} className="flex-1 md:flex-none bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold px-4 md:px-3 py-2.5 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center whitespace-nowrap" title={isZh ? '筛选' : 'Filters'}>
                      <SlidersHorizontal className="h-5 w-5" />
                    </button>
                    <button onClick={handleSearch} disabled={isLoading} className="flex-1 md:flex-none bg-primary hover:bg-primary/90 text-white font-semibold px-4 md:px-3 py-2.5 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed" title={isZh ? '搜索' : 'Search'}>
                      <Search className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Filters Modal */}
      <Modal
        isOpen={showAdvanced}
        onClose={() => {
          setTempAdvancedFilters(advancedFilters);
          setShowAdvanced(false);
        }}
        title={isZh ? '进阶筛选' : 'Advanced Filters'}
        maxWidth="max-w-2xl"
        footer={
          <div className="flex gap-4">
            <button
              onClick={() => {
                setTempAdvancedFilters(advancedFilters);
                setShowAdvanced(false);
              }}
              className="flex-1 px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-all duration-200"
            >
              {isZh ? '取消' : 'Cancel'}
            </button>
            <button
              onClick={() => {
                setAdvancedFilters(tempAdvancedFilters);
                setCurrentPage(1);
                setShowAdvanced(false);
              }}
              className="flex-1 px-6 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {isZh ? '应用筛选' : 'Apply Filters'}
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isZh ? '教学经验年数' : 'Years of Experience'}
            </label>
            <SearchableDropdown
              options={[
                { value: '', label: isZh ? '不限' : 'Any' },
                ...TUITION_EXPERIENCE_YEARS.map((opt) => ({ value: String(opt.value), label: opt.label })),
              ]}
              value={tempAdvancedFilters.yearsOfExperience}
              onChange={(value) => setTempAdvancedFilters({ ...tempAdvancedFilters, yearsOfExperience: String(value) })}
              placeholder={isZh ? '选择经验' : 'Select experience'}
              searchable={false}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isZh ? '性别' : 'Gender'}
            </label>
            <SearchableDropdown
              options={[
                { value: '', label: isZh ? '不限' : 'Any' },
                { value: 'male', label: localizeText(locale, 'Male') },
                { value: 'female', label: localizeText(locale, 'Female') }
              ]}
              value={tempAdvancedFilters.gender}
              onChange={(value) => setTempAdvancedFilters({ ...tempAdvancedFilters, gender: String(value) })}
              placeholder={isZh ? '选择性别' : 'Select gender'}
              searchable={false}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isZh ? '授课方式' : 'Mode of Tuition'}
            </label>
            <SearchableDropdown
              options={[
                { value: '', label: isZh ? '不限' : 'Any' },
                { value: 'physical', label: localizeText(locale, 'Physical') },
                { value: 'online', label: localizeText(locale, 'Online') },
                { value: 'both', label: localizeText(locale, 'Both') }
              ]}
              value={tempAdvancedFilters.tuitionType}
              onChange={(value) => setTempAdvancedFilters({ ...tempAdvancedFilters, tuitionType: String(value) })}
              placeholder={isZh ? '选择方式' : 'Select mode'}
              searchable={false}
            />
          </div>
          <div className="md:col-span-2">
            <DualRangeSlider
              min={0}
              max={200}
              minValue={tempAdvancedFilters.priceMin ? parseInt(tempAdvancedFilters.priceMin) : 0}
              maxValue={tempAdvancedFilters.priceMax ? parseInt(tempAdvancedFilters.priceMax) : 200}
              onChange={(minValue, maxValue) => setTempAdvancedFilters({ ...tempAdvancedFilters, priceMin: String(minValue), priceMax: String(maxValue) })}
              label={
                <span className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-1" />
                  {isZh ? '价格范围 (RM/小时)' : 'Price Range (RM/hour)'}
                </span>
              }
            />
          </div>
        </div>
      </Modal>

      <WishlistCaseSelectionModal
        isOpen={Boolean(wishlistTutor)}
        tutorId={wishlistTutor?.id}
        tutorName={wishlistTutor?.name}
        onClose={() => setWishlistTutor(null)}
      />

      {/* Results Section */}
      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="mb-4 flex items-center text-red-600">
            <AlertCircle className="mr-2 h-4 w-4" />
            {error.message || (isZh ? '发生错误' : 'An error occurred')}
          </div>
        )}

        {/* Tutor Cards */}
        {isLoading ? (
          <CaseCardSkeletonGrid count={12} />
        ) : tutors.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{isZh ? '找不到符合条件的导师，请尝试调整筛选条件。' : 'No tutors found matching your criteria. Try adjusting your filters.'}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tutors.map((tutor) => {
                const isWishlisted = getOptimisticWishlistState(
                  tutor.id,
                  tutor.isWishlisted,
                  wishlistOverlayMap
                );
                const isMutating = wishlistOverlayMap.has(tutor.id);

                return (
                  <TutorCard
                    key={tutor.id}
                    tutor={tutor}
                    isWishlisted={isWishlisted}
                    isMutating={isMutating}
                    onToggleWishlist={handleToggleWishlist}
                    source="browse"
                    returnSearch={persistedSearchParams.toString()}
                  />
                );
              })}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              isLoading={isLoading}
            />
          </>
        )}
      </div>
    </div>
    </>
  );
};

export default AllTutorsPage;
